import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useFormatCurrency } from "@/hooks/use-format-currency";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  X,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Plus,
  Download,
  Settings2,
  Filter,
  Warehouse,
  MapPin,
  Edit,
  Power,
  Upload,
  Grid3x3,
  FileSpreadsheet,
  Activity,
  Package,
  Loader2,
  Monitor,
  TestTube2,
} from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { mockWarehouses, Warehouse as WarehouseType, Location } from "@/data/mockWarehousesLocations";
import { getWarehouseLocations, getStockItems } from "@/services/stockDataService";
import { WarehouseFormDialog } from "@/components/warehouses/warehouse-form-dialog";
import { LocationFormDialog } from "@/components/warehouses/location-form-dialog-dynamic";
import { LocationDetailsDrawer } from "@/components/warehouses/location-details-drawer-dynamic";
import { WarehouseTypesSettingsDialog } from "@/components/warehouses/warehouse-types-settings-dialog";
import { WarehouseItemsDrawer } from "@/components/warehouses/warehouse-items-drawer";
import { StaticMetricCardAlt } from "@/components/StaticMetricCardAlt";
import { BulkCreateLocationsDialog } from "@/components/warehouses/bulk-create-locations-dialog";
import { ImportLocationsDialog } from "@/components/warehouses/import-locations-dialog";
import { DeactivateWarehouseDialog } from "@/components/warehouses/deactivate-warehouse-dialog";
import { DeactivateLocationDialog } from "@/components/warehouses/deactivate-location-dialog";
import { ConfigureColumnsModal, ColumnDefinition } from "@/components/configure-columns-modal";
import { AdvancedFiltersModal } from "@/components/advanced-filters-modal";
import {
  exportToCSV,
  exportToExcel,
  getCurrentDateForFilename,
  prepareDataForExport,
  getVisibleColumns,
  getAllColumns,
} from "@/lib/export-utils";
import { useContextualHelp } from "@/hooks/use-contextual-help";
import { HelpButton } from "@/components/help-button";
import { ContextualHelpDrawer } from "@/components/contextual-help-drawer";
import { useScreenMode } from "@/providers/ModeProvider";
import { useWarehousesLocations, LocationSortInput, LocationFilterInput } from "@/hooks/use-warehouses-locations";
import { FilterRow, FilterField } from "@/types/filters";
import { useWarehouseHierarchyConfig } from "@/hooks/use-warehouse-hierarchy-config";
import { useWarehouseStockValue } from "@/hooks/use-warehouse-stock-value";

// Helper function to convert FilterRow[] to GraphQL filter input
// The backend supports filtering by locationCode, locationType, and active
// Note: Backend StringFilterInput uses 'beginsWith' (not 'startsWith'), 'ne' (not 'notEquals'),
// 'isBlank'/'isNotBlank' (not 'isEmpty'/'isNotEmpty'), 'doesNotContain', 'endsWith', etc.
// Helper function to map a text filter row to GraphQL string filter format
const mapTextFilterToGraphQL = (row: FilterRow): Record<string, string | boolean> | null => {
  switch (row.operator) {
    case "contains":
      return row.value ? { contains: String(row.value) } : null;
    case "doesNotContain":
      return row.value ? { doesNotContain: String(row.value) } : null;
    case "equals":
      return row.value ? { eq: String(row.value) } : null;
    case "notEquals":
      return row.value ? { ne: String(row.value) } : null;
    case "startsWith":
      // Backend uses 'beginsWith' not 'startsWith'
      return row.value ? { beginsWith: String(row.value) } : null;
    case "endsWith":
      return row.value ? { endsWith: String(row.value) } : null;
    case "isEmpty":
      // Backend uses 'isBlank' not 'isEmpty'
      return { isBlank: true };
    case "isNotEmpty":
      // Backend uses 'isNotBlank' not 'isNotEmpty'
      return { isNotBlank: true };
    default:
      return null;
  }
};

const mapFilterRowsToGraphQL = (filterRows: FilterRow[]): LocationFilterInput => {
  const filterInput: LocationFilterInput = {};

  for (const row of filterRows) {
    const textFilter = mapTextFilterToGraphQL(row);

    // Map field names to GraphQL filter fields
    switch (row.field) {
      case "code":
      case "locationCode":
        if (textFilter) filterInput.locationCode = textFilter;
        break;
      case "locationType":
      case "type":
        if (textFilter) filterInput.locationType = textFilter;
        break;
      case "aisle":
        if (textFilter) filterInput.aisle = textFilter;
        break;
      case "rack":
        if (textFilter) filterInput.rack = textFilter;
        break;
      case "shelf":
        if (textFilter) filterInput.shelf = textFilter;
        break;
      case "bin":
        if (textFilter) filterInput.bin = textFilter;
        break;
      case "active":
      case "status":
        // Active status - map to boolean
        if (row.operator === "isYes") {
          filterInput.active = { eq: true };
        } else if (row.operator === "isNo") {
          filterInput.active = { eq: false };
        } else if (row.value === "Active") {
          filterInput.active = { eq: true };
        } else if (row.value === "Blocked" || row.value === "Inactive") {
          filterInput.active = { eq: false };
        }
        break;
    }
  }

  return filterInput;
};

// Generate dynamic columns based on hierarchy configuration
const generateLocationColumns = (hierarchyConfig: any): ColumnDefinition[] => {
  const baseColumns: ColumnDefinition[] = [
    { id: "locationCode", name: "Location Code", description: "Unique location identifier" },
    { id: "locationType", name: "Type", description: "Location type (Storage, Picking, etc.)" },
  ];

  // Add hierarchy level columns
  const activeLevels = hierarchyConfig.activeLevels || [];
  const hierarchyColumns = activeLevels.map((level: any) => ({
    id: `level${level.level}`,
    name: level.displayName,
    description: `${level.displayName} code`,
  }));

  const otherColumns: ColumnDefinition[] = [
    { id: "currentStock", name: "Current Stock", description: "Current stock quantity" },
    { id: "pickingSequence", name: "Picking Sequence", description: "Order for picking optimisation" },
    { id: "status", name: "Status", description: "Active or Inactive" },
    { id: "lastActivity", name: "Last Activity", description: "Last stock movement date" },
  ];

  return [...baseColumns, ...hierarchyColumns, ...otherColumns];
};

// Generate default visible columns based on hierarchy
const generateDefaultVisibleColumns = (hierarchyConfig: any): string[] => {
  const baseColumns = ["locationCode", "locationType"];
  const hierarchyColumns = (hierarchyConfig.activeLevels || []).map((level: any) => `level${level.level}`);
  const otherColumns = ["status"];
  return [...baseColumns, ...hierarchyColumns, ...otherColumns];
};

type LocationSortColumn = "locationCode" | "locationType" | "pickingSequence";
type SortDirection = "asc" | "desc";

// Advanced filter sections configuration (defined outside component to prevent recreation)
const LOCATION_ADVANCED_FILTER_SECTIONS = [
  {
    title: "Location Hierarchy",
    fields: [
      { id: "aisle", label: "Aisle", type: "text" as const, placeholder: "e.g., A01" },
      { id: "rack", label: "Rack", type: "text" as const, placeholder: "e.g., R01" },
      { id: "shelf", label: "Shelf", type: "text" as const, placeholder: "e.g., S01" },
      { id: "bin", label: "Bin", type: "text" as const, placeholder: "e.g., B01" },
    ],
  },
  {
    title: "Activity",
    fields: [
      {
        id: "lastActivity",
        label: "Last Activity",
        type: "select" as const,
        options: [
          { value: "any", label: "Any time" },
          { value: "7days", label: "Last 7 days" },
          { value: "30days", label: "Last 30 days" },
          { value: "90days", label: "Last 90 days" },
          { value: "90plus", label: "90+ days ago" },
          { value: "never", label: "Never used" },
        ],
      },
    ],
  },
  {
    title: "Picking",
    fields: [
      { id: "pickingSequenceFrom", label: "Picking Sequence From", type: "number" as const, placeholder: "Min" },
      { id: "pickingSequenceTo", label: "Picking Sequence To", type: "number" as const, placeholder: "Max" },
    ],
  },
  {
    title: "Type & Status",
    fields: [
      {
        id: "locationTypes",
        label: "Location Types",
        type: "multiselect" as const,
        options: [
          { value: "Storage", label: "Storage" },
          { value: "Picking", label: "Picking" },
          { value: "Receiving", label: "Receiving" },
          { value: "Shipping", label: "Shipping" },
          { value: "Quarantine", label: "Quarantine" },
          { value: "Returns", label: "Returns" },
          { value: "Staging", label: "Staging" },
        ],
      },
      {
        id: "locationStatus",
        label: "Location Status",
        type: "multiselect" as const,
        options: [
          { value: "Active", label: "Active" },
          { value: "Blocked", label: "Blocked" },
          { value: "Inactive", label: "Inactive" },
        ],
      },
    ],
  },
  {
    title: "Stock",
    fields: [
      {
        id: "hasStockItems",
        label: "Has Stock Items",
        type: "select" as const,
        options: [
          { value: "any", label: "Any" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
  {
    title: "Additional",
    fields: [
      {
        id: "hasNotes",
        label: "Has Notes",
        type: "select" as const,
        options: [
          { value: "any", label: "Any" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
];

export function WarehousesLocationsPage() {
  // Currency formatting
  const formatCurrency = useFormatCurrency();

  // Help system
  const { isHelpOpen, helpContent, toggleHelp, setIsHelpOpen } = useContextualHelp("warehouses");

  // Mode context for demo/live toggle (screen-specific)
  const { setMode, isDemo, isLive } = useScreenMode("warehouses-locations");
  const isLiveMode = isLive;

  // Warehouse hierarchy configuration - pass isLive to fetch from API when in live mode
  const hierarchyConfig = useWarehouseHierarchyConfig({ isLive: isLiveMode });

  // Generate dynamic columns based on hierarchy config
  const ALL_LOCATION_COLUMNS = useMemo(() => {
    return generateLocationColumns(hierarchyConfig);
  }, [hierarchyConfig.activeLevels]);

  const DEFAULT_VISIBLE_LOCATION_COLUMNS = useMemo(() => {
    return generateDefaultVisibleColumns(hierarchyConfig);
  }, [hierarchyConfig.activeLevels]);

  // Generate dynamic filter fields based on hierarchy config
  // The default hierarchy is: Level 1=Warehouse, Level 2=Aisle, Level 3=Rack, Level 4=Shelf, Level 5=Bin
  // We map levels 2-5 to their corresponding backend fields (aisle, rack, shelf, bin)
  // Level 1 (Warehouse) is not a filterable location field - it's the warehouse selector
  const dynamicFilterFields = useMemo((): FilterField[] => {
    const levelToFieldMap: Record<number, string> = {
      2: "aisle",
      3: "rack",
      4: "shelf",
      5: "bin",
    };

    // Base fields that are always available
    const baseFields: FilterField[] = [
      { id: "locationCode", label: "Location Code", type: "text" },
      { id: "locationType", label: "Type", type: "text" },
    ];

    // Dynamic hierarchy fields based on active levels (exclude level 1 which is Warehouse)
    const hierarchyFields: FilterField[] = hierarchyConfig.activeLevels
      .filter((level) => level.level >= 2 && level.level <= 5)
      .map((level) => ({
        id: levelToFieldMap[level.level],
        label: level.displayName,
        type: "text" as const,
      }));

    // Additional fields
    const additionalFields: FilterField[] = [
      {
        id: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "Active", label: "Active" },
          { value: "Blocked", label: "Blocked" },
          { value: "Inactive", label: "Inactive" },
        ],
      },
      { id: "currentStock", label: "Current Stock", type: "number" },
      { id: "pickingSequence", label: "Picking Sequence", type: "number" },
    ];

    return [...baseFields, ...hierarchyFields, ...additionalFields];
  }, [hierarchyConfig.activeLevels]);

  // Backend data hook
  const {
    warehouses: backendWarehouses,
    locations: backendLocations,
    locationsPageInfo,
    isLoadingWarehouses,
    isLoadingLocations,
    fetchWarehouses,
    fetchLocations,
    deactivateWarehouse,
    activateWarehouse,
    createLocation,
    updateLocation,
    bulkCreateLocations,
    deactivateLocation,
    blockLocation,
    unblockLocation,
    createWarehouse,
    updateWarehouse,
  } = useWarehousesLocations();

  // Stock value hook for live mode
  const { stockValue: liveStockValue, isLoading: isLoadingStockValue, fetchStockValue } = useWarehouseStockValue();

  // Warehouse state (for demo mode)
  const [mockWarehouseData, setMockWarehouseData] = useState<WarehouseType[]>(mockWarehouses);
  const [selectedWarehouseCode, setSelectedWarehouseCode] = useState<string>(mockWarehouses[0]?.code || "");
  const [isWarehouseFormOpen, setIsWarehouseFormOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<WarehouseType | null>(null);
  const [isDeactivateWarehouseOpen, setIsDeactivateWarehouseOpen] = useState(false);
  const [warehouseToDeactivate, setWarehouseToDeactivate] = useState<WarehouseType | null>(null);
  const [isWarehouseTypesSettingsOpen, setIsWarehouseTypesSettingsOpen] = useState(false);

  // Location state (for demo mode)
  const [mockLocationData, setMockLocationData] = useState<Location[]>(getWarehouseLocations());
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<LocationSortColumn>("locationCode");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());

  // Advanced filters state
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<Record<string, any>>({});
  const [activeFilterRows, setActiveFilterRows] = useState<any[]>([]);
  const [activeFilterLogic, setActiveFilterLogic] = useState<any[]>([]);

  // Calculate active advanced filters count
  const activeAdvancedFiltersCount = activeFilterRows.length;

  // Cursor-based pagination state for live mode
  const [cursors, setCursors] = useState<string[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(0);

  // Get the current data source based on mode
  const warehouses = useMemo(() => {
    return isLiveMode ? backendWarehouses : mockWarehouseData;
  }, [isLiveMode, backendWarehouses, mockWarehouseData]);

  const locations = useMemo(() => {
    return isLiveMode ? backendLocations : mockLocationData;
  }, [isLiveMode, backendLocations, mockLocationData]);

  // Handle mode toggle
  const handleModeToggle = useCallback(
    (newIsLive: boolean) => {
      setMode(newIsLive ? "live" : "demo");
      setCurrentPage(1);
      setCursors([]);
      setCurrentCursorIndex(0);
      setSelectedLocations(new Set());
    },
    [setMode],
  );

  // Fetch data when in live mode
  useEffect(() => {
    if (isLiveMode) {
      fetchWarehouses();
    }
  }, [isLiveMode, fetchWarehouses]);

  // Update selectedWarehouseCode when switching to live mode and warehouses are loaded
  useEffect(() => {
    if (isLiveMode && backendWarehouses.length > 0) {
      const currentWarehouseExists = backendWarehouses.some((w) => w.code === selectedWarehouseCode);
      if (!currentWarehouseExists) {
        setSelectedWarehouseCode(backendWarehouses[0].code);
      }
    }
  }, [isLiveMode, backendWarehouses, selectedWarehouseCode]);

  // Fetch stock value when in live mode and warehouse changes
  useEffect(() => {
    if (isLiveMode && selectedWarehouseCode) {
      fetchStockValue(selectedWarehouseCode);
    }
  }, [isLiveMode, selectedWarehouseCode, fetchStockValue]);

  // Track previous filter values to detect changes and reset pagination
  const prevFiltersRef = useRef<{
    warehouseCode: string;
    searchQuery: string;
    filterRows: string;
    sortColumn: string;
    sortDirection: string;
  } | null>(null);

  // Fetch locations when warehouse changes in live mode
  // This effect handles both pagination reset and fetching in one place to avoid duplicate queries
  useEffect(() => {
    if (isLiveMode && selectedWarehouseCode) {
      // Check if filter-related values have changed (not pagination)
      const currentFilters = {
        warehouseCode: selectedWarehouseCode,
        searchQuery: debouncedSearchQuery,
        filterRows: JSON.stringify(activeFilterRows),
        sortColumn,
        sortDirection,
      };

      const filtersChanged =
        prevFiltersRef.current !== null &&
        (prevFiltersRef.current.warehouseCode !== currentFilters.warehouseCode ||
          prevFiltersRef.current.searchQuery !== currentFilters.searchQuery ||
          prevFiltersRef.current.filterRows !== currentFilters.filterRows ||
          prevFiltersRef.current.sortColumn !== currentFilters.sortColumn ||
          prevFiltersRef.current.sortDirection !== currentFilters.sortDirection);

      // Update the ref with current values
      prevFiltersRef.current = currentFilters;

      // If filters changed, reset pagination and fetch from the beginning
      // Otherwise, use the current cursor for pagination
      let effectiveCursorIndex = currentCursorIndex;
      if (filtersChanged) {
        setCursors([]);
        setCurrentCursorIndex(0);
        effectiveCursorIndex = 0;
      }

      // Build sort input based on current sort state - using backend SortBy format
      let sortInput: LocationSortInput | undefined = undefined;
      if (sortColumn === "locationCode") {
        sortInput = { field: "LocationCode", order: sortDirection === "asc" ? "Ascending" : "Descending" };
      } else if (sortColumn === "locationType") {
        sortInput = { field: "LocationType", order: sortDirection === "asc" ? "Ascending" : "Descending" };
      } else if (sortColumn === "pickingSequence") {
        sortInput = { field: "PickingSequence", order: sortDirection === "asc" ? "Ascending" : "Descending" };
      }

      // Build filter input - start with search query filter
      const filterInput: LocationFilterInput = {};
      if (debouncedSearchQuery.trim()) {
        filterInput.locationCode = { contains: debouncedSearchQuery };
      }

      // Merge advanced filters (from FilterRow[]) into the filter input
      const advancedFilterInput = mapFilterRowsToGraphQL(activeFilterRows);
      const mergedFilterInput: LocationFilterInput = { ...filterInput, ...advancedFilterInput };

      // Get the cursor for the current page (use effective index after potential reset)
      const after = effectiveCursorIndex > 0 ? cursors[effectiveCursorIndex - 1] : undefined;

      fetchLocations({
        first: itemsPerPage,
        after,
        warehouseCode: selectedWarehouseCode,
        filter: Object.keys(mergedFilterInput).length > 0 ? mergedFilterInput : undefined,
        sort: sortInput,
      }).then(({ pageInfo }) => {
        // Store the end cursor for next page navigation
        if (pageInfo?.endCursor && effectiveCursorIndex === cursors.length) {
          setCursors((prev) => [...prev, pageInfo.endCursor!]);
        }
      });
    }
  }, [
    isLiveMode,
    selectedWarehouseCode,
    debouncedSearchQuery,
    activeFilterRows,
    sortColumn,
    sortDirection,
    itemsPerPage,
    currentCursorIndex,
    cursors,
    fetchLocations,
  ]);

  // Location dialogs
  const [isLocationFormOpen, setIsLocationFormOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [isLocationDrawerOpen, setIsLocationDrawerOpen] = useState(false);
  const [selectedDrawerLocation, setSelectedDrawerLocation] = useState<Location | null>(null);
  const [isBulkCreateOpen, setIsBulkCreateOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isDeactivateLocationOpen, setIsDeactivateLocationOpen] = useState(false);
  const [locationToDeactivate, setLocationToDeactivate] = useState<Location | null>(null);

  // Warehouse items drawer state
  const [isWarehouseItemsDrawerOpen, setIsWarehouseItemsDrawerOpen] = useState(false);

  // Configure columns state
  const [isConfigureColumnsModalOpen, setIsConfigureColumnsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_LOCATION_COLUMNS);
  const [columnOrder, setColumnOrder] = useState<string[]>(DEFAULT_VISIBLE_LOCATION_COLUMNS);

  // Get visible columns in the correct order based on columnOrder
  const getOrderedVisibleColumns = useCallback(() => {
    return columnOrder.filter((colId) => visibleColumns.includes(colId));
  }, [columnOrder, visibleColumns]);

  // Loading states
  const [isExporting, setIsExporting] = useState(false);

  // Get selected warehouse
  const selectedWarehouse = useMemo(() => {
    return warehouses.find((w) => w.code === selectedWarehouseCode);
  }, [warehouses, selectedWarehouseCode]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchQuery, selectedWarehouseCode]);

  // Filter locations by warehouse and search/filters
  const filteredLocations = useMemo(() => {
    let filtered = locations.filter((loc) => loc.warehouseCode === selectedWarehouseCode);

    // Apply unified search (location code OR item code/name)
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase();
      const stockItems = getStockItems();

      // Find locations that match by location code OR have items matching the query
      const locationCodesWithMatchingItems = stockItems
        .filter((item) => item.itemCode.toLowerCase().includes(query) || item.itemName.toLowerCase().includes(query))
        .map((item) => item.locationCode);

      filtered = filtered.filter(
        (loc) =>
          loc.locationCode.toLowerCase().includes(query) || // Match location code
          locationCodesWithMatchingItems.includes(loc.locationCode), // OR has matching item
      );
    }

    // Apply advanced filters
    if (Object.keys(advancedFilters).length > 0) {
      const stockItems = getStockItems();

      filtered = filtered.filter((loc) => {
        // Location hierarchy filters
        if (
          advancedFilters.aisle &&
          !loc.hierarchyData?.aisle?.toLowerCase().includes(advancedFilters.aisle.toLowerCase())
        ) {
          return false;
        }
        if (
          advancedFilters.rack &&
          !loc.hierarchyData?.rack?.toLowerCase().includes(advancedFilters.rack.toLowerCase())
        ) {
          return false;
        }
        if (
          advancedFilters.shelf &&
          !loc.hierarchyData?.shelf?.toLowerCase().includes(advancedFilters.shelf.toLowerCase())
        ) {
          return false;
        }
        if (advancedFilters.bin && !loc.hierarchyData?.bin?.toLowerCase().includes(advancedFilters.bin.toLowerCase())) {
          return false;
        }

        // Last activity filter
        if (advancedFilters.lastActivity && advancedFilters.lastActivity !== "any") {
          const now = new Date();
          const lastActivity = loc.lastActivityDate ? new Date(loc.lastActivityDate) : null;

          if (advancedFilters.lastActivity === "never" && lastActivity !== null) return false;
          if (advancedFilters.lastActivity !== "never" && lastActivity === null) return false;

          if (lastActivity) {
            const daysDiff = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24));

            if (advancedFilters.lastActivity === "7days" && daysDiff > 7) return false;
            if (advancedFilters.lastActivity === "30days" && daysDiff > 30) return false;
            if (advancedFilters.lastActivity === "90days" && daysDiff > 90) return false;
            if (advancedFilters.lastActivity === "90plus" && daysDiff <= 90) return false;
          }
        }

        // Picking sequence filters
        if (
          advancedFilters.pickingSeqFrom &&
          (!loc.pickingSequence || loc.pickingSequence < advancedFilters.pickingSeqFrom)
        ) {
          return false;
        }
        if (
          advancedFilters.pickingSeqTo &&
          (!loc.pickingSequence || loc.pickingSequence > advancedFilters.pickingSeqTo)
        ) {
          return false;
        }

        // Location types filter
        if (advancedFilters.locationTypes && advancedFilters.locationTypes.length > 0) {
          if (!advancedFilters.locationTypes.includes(loc.locationType)) {
            return false;
          }
        }

        // Location status filter
        if (advancedFilters.locationStatus && advancedFilters.locationStatus.length > 0) {
          if (!advancedFilters.locationStatus.includes(loc.status)) {
            return false;
          }
        }

        // Has stock items filter
        if (advancedFilters.hasStockItems && advancedFilters.hasStockItems !== "any") {
          const locationStockItems = stockItems.filter((item) => item.locationCode === loc.locationCode);
          const hasStock = locationStockItems.length > 0;

          if (advancedFilters.hasStockItems === "yes" && !hasStock) return false;
          if (advancedFilters.hasStockItems === "no" && hasStock) return false;
        }

        // Has notes filter
        if (advancedFilters.hasNotes && advancedFilters.hasNotes !== "any") {
          const hasNotes = loc.notes && loc.notes.trim() !== "";

          if (advancedFilters.hasNotes === "yes" && !hasNotes) return false;
          if (advancedFilters.hasNotes === "no" && hasNotes) return false;
        }

        return true;
      });
    }

    return filtered;
  }, [locations, selectedWarehouseCode, debouncedSearchQuery, advancedFilters]);

  // Sort locations
  const sortedLocations = useMemo(() => {
    const sorted = [...filteredLocations];

    sorted.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortColumn) {
        case "locationCode":
          aValue = a.locationCode;
          bValue = b.locationCode;
          break;
        case "locationType":
          aValue = a.locationType;
          bValue = b.locationType;
          break;
        case "pickingSequence":
          aValue = a.pickingSequence || 0;
          bValue = b.pickingSequence || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredLocations, sortColumn, sortDirection]);

  // Paginate locations
  const paginatedLocations = useMemo(() => {
    // In live mode, data is already paginated from the server
    if (isLiveMode) {
      return sortedLocations;
    }
    // In demo mode, paginate locally
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedLocations.slice(startIndex, endIndex);
  }, [sortedLocations, currentPage, itemsPerPage, isLiveMode]);

  // Calculate pagination values
  const totalPages = Math.ceil(sortedLocations.length / itemsPerPage);
  const startItem = sortedLocations.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, sortedLocations.length);

  // Handle sorting
  const handleSort = (column: LocationSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Render sort icon
  const renderSortIcon = (column: LocationSortColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? (
      <ArrowUp className="h-4 w-4 inline ml-1" />
    ) : (
      <ArrowDown className="h-4 w-4 inline ml-1" />
    );
  };

  // Render table header cell based on column ID
  const renderTableHeaderCell = (columnId: string) => {
    switch (columnId) {
      case "locationCode":
        return (
          <TableHead
            key={columnId}
            className="font-semibold cursor-pointer hover:bg-muted/50"
            onClick={() => handleSort("locationCode")}
          >
            Location Code {renderSortIcon("locationCode")}
          </TableHead>
        );
      case "locationType":
        return (
          <TableHead
            key={columnId}
            className="font-semibold cursor-pointer hover:bg-muted/50"
            onClick={() => handleSort("locationType")}
          >
            Type {renderSortIcon("locationType")}
          </TableHead>
        );
      case "currentStock":
        return (
          <TableHead key={columnId} className="font-semibold text-right">
            Current Stock
          </TableHead>
        );
      case "pickingSequence":
        return (
          <TableHead
            key={columnId}
            className="font-semibold text-right cursor-pointer hover:bg-muted/50"
            onClick={() => handleSort("pickingSequence")}
          >
            Picking Seq {renderSortIcon("pickingSequence")}
          </TableHead>
        );
      case "status":
        return (
          <TableHead key={columnId} className="font-semibold">
            Status
          </TableHead>
        );
      case "lastActivity":
        return (
          <TableHead key={columnId} className="font-semibold">
            Last Activity
          </TableHead>
        );
      default:
        // Handle dynamic hierarchy columns (level1, level2, etc.)
        if (columnId.startsWith("level")) {
          const levelNum = parseInt(columnId.replace("level", ""), 10);
          const level = hierarchyConfig.activeLevels.find((l) => l.level === levelNum);
          if (level) {
            return (
              <TableHead key={columnId} className="font-semibold">
                {level.displayName}
              </TableHead>
            );
          }
        }
        return null;
    }
  };

  // Render table cell based on column ID
  const renderTableCell = (location: Location, columnId: string) => {
    switch (columnId) {
      case "locationCode":
        return (
          <TableCell key={columnId} className="font-medium font-mono">
            {location.locationCode}
          </TableCell>
        );
      case "locationType":
        return (
          <TableCell key={columnId}>
            <Badge variant="outline">{location.locationType}</Badge>
          </TableCell>
        );
      case "currentStock":
        return (
          <TableCell key={columnId} className="text-right font-mono">
            {location.currentStock.toLocaleString()}
          </TableCell>
        );
      case "pickingSequence":
        return (
          <TableCell key={columnId} className="text-right font-mono">
            {location.pickingSequence || "-"}
          </TableCell>
        );
      case "status":
        return (
          <TableCell key={columnId}>
            {location.status === "Active" && (
              <Badge variant="success" accent="success">
                Active
              </Badge>
            )}
            {location.status === "Blocked" && <Badge className="bg-red-500 text-white hover:bg-red-600">Blocked</Badge>}
            {location.status === "Inactive" && (
              <Badge className="bg-gray-500 text-white hover:bg-gray-600">Inactive</Badge>
            )}
            {!location.status && <Badge variant="secondary">Active</Badge>}
          </TableCell>
        );
      case "lastActivity":
        return (
          <TableCell key={columnId}>
            {location.lastActivityDate ? (
              <span className="text-sm text-muted-foreground">
                {(() => {
                  const now = new Date();
                  const activityDate = new Date(location.lastActivityDate);
                  const diffMs = now.getTime() - activityDate.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);
                  const diffWeeks = Math.floor(diffDays / 7);
                  const diffMonths = Math.floor(diffDays / 30);

                  if (diffMins < 1) return "Just now";
                  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? "s" : ""} ago`;
                  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
                  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
                  if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
                  return `${diffMonths} month${diffMonths > 1 ? "s" : ""} ago`;
                })()}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">Never</span>
            )}
          </TableCell>
        );
      default:
        // Handle dynamic hierarchy columns (level1, level2, etc.)
        if (columnId.startsWith("level")) {
          const levelNum = parseInt(columnId.replace("level", ""), 10);
          const level = hierarchyConfig.activeLevels.find((l) => l.level === levelNum);
          if (level) {
            // Get value from hierarchyData or legacy fields
            let value = location.hierarchyData?.[level.level];
            if (!value) {
              // Fallback to legacy fields for backward compatibility
              // Level 1 = Warehouse, Level 2 = Aisle, Level 3 = Rack, Level 4 = Shelf, Level 5 = Bin
              if (level.level === 1) value = location.warehouseCode;
              else if (level.level === 2) value = location.aisle;
              else if (level.level === 3) value = location.rack;
              else if (level.level === 4) value = location.shelf;
              else if (level.level === 5) value = location.bin;
            }
            return (
              <TableCell key={columnId} className="font-mono">
                {value || "-"}
              </TableCell>
            );
          }
        }
        return null;
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelection = new Set(selectedLocations);
      paginatedLocations.forEach((loc) => newSelection.add(loc.locationCode));
      setSelectedLocations(newSelection);
    } else {
      const newSelection = new Set(selectedLocations);
      paginatedLocations.forEach((loc) => newSelection.delete(loc.locationCode));
      setSelectedLocations(newSelection);
    }
  };

  const handleSelectLocation = (code: string, checked: boolean) => {
    const newSelection = new Set(selectedLocations);
    if (checked) {
      newSelection.add(code);
    } else {
      newSelection.delete(code);
    }
    setSelectedLocations(newSelection);
  };

  const allPageItemsSelected =
    paginatedLocations.length > 0 && paginatedLocations.every((loc) => selectedLocations.has(loc.locationCode));
  const somePageItemsSelected =
    paginatedLocations.some((loc) => selectedLocations.has(loc.locationCode)) && !allPageItemsSelected;

  // Warehouse handlers
  const handleAddWarehouse = () => {
    setEditingWarehouse(null);
    setIsWarehouseFormOpen(true);
  };

  const handleEditWarehouse = () => {
    if (selectedWarehouse) {
      setEditingWarehouse(selectedWarehouse);
      setIsWarehouseFormOpen(true);
    }
  };

  const handleSaveWarehouse = async (warehouse: WarehouseType) => {
    if (isLiveMode) {
      // Live mode - call backend mutation
      const warehouseInput = {
        warehouseCode: warehouse.code,
        warehouseName: warehouse.name,
        address: warehouse.address || undefined,
        warehouseType: warehouse.type,
        totalCapacity: warehouse.totalCapacity || undefined,
        defaultReceiveLocation: warehouse.defaultReceiveLocation || undefined,
        defaultPickLocation: warehouse.defaultPickLocation || undefined,
      };

      if (editingWarehouse && warehouse.id) {
        // Update existing warehouse
        const result = await updateWarehouse(warehouse.id, warehouseInput);
        if (result.success) {
          toast.success("Warehouse updated successfully");
          // Refresh warehouses list
          fetchWarehouses();
        } else {
          toast.error(result.error || "Failed to update warehouse");
          return;
        }
      } else {
        // Create new warehouse
        const result = await createWarehouse(warehouseInput);
        if (result.success) {
          toast.success("Warehouse created successfully");
          // Refresh warehouses list
          fetchWarehouses();
        } else {
          toast.error(result.error || "Failed to create warehouse");
          return;
        }
      }
    } else {
      // Demo mode - update mock data
      if (editingWarehouse) {
        setMockWarehouseData(mockWarehouseData.map((w) => (w.code === warehouse.code ? warehouse : w)));
        toast.success("Warehouse updated successfully");
      } else {
        setMockWarehouseData([...mockWarehouseData, warehouse]);
        setSelectedWarehouseCode(warehouse.code);
        toast.success("Warehouse created successfully");
      }
    }
    setIsWarehouseFormOpen(false);
    setEditingWarehouse(null);
  };

  const handleDeactivateWarehouse = () => {
    if (selectedWarehouse) {
      setWarehouseToDeactivate(selectedWarehouse);
      setIsDeactivateWarehouseOpen(true);
    }
  };

  const confirmDeactivateWarehouse = async () => {
    if (warehouseToDeactivate) {
      if (isLiveMode && warehouseToDeactivate.id) {
        const result = await deactivateWarehouse(warehouseToDeactivate.id);
        if (result.success) {
          toast.success("Warehouse deactivated successfully");
        } else {
          toast.error(result.error || "Failed to deactivate warehouse");
        }
      } else {
        setMockWarehouseData(
          mockWarehouseData.map((w) => (w.code === warehouseToDeactivate.code ? { ...w, active: false } : w)),
        );
        toast.success("Warehouse deactivated successfully");
      }
      setIsDeactivateWarehouseOpen(false);
      setWarehouseToDeactivate(null);
    }
  };

  const handleActivateWarehouse = async () => {
    if (selectedWarehouse) {
      if (isLiveMode && selectedWarehouse.id) {
        const result = await activateWarehouse(selectedWarehouse.id);
        if (result.success) {
          toast.success("Warehouse activated successfully");
        } else {
          toast.error(result.error || "Failed to activate warehouse");
        }
      } else {
        setMockWarehouseData(
          mockWarehouseData.map((w) => (w.code === selectedWarehouse.code ? { ...w, active: true } : w)),
        );
        toast.success("Warehouse activated successfully");
      }
    }
  };

  // Location handlers
  const handleAddLocation = () => {
    setEditingLocation(null);
    setIsLocationFormOpen(true);
  };

  const handleSaveLocation = async (location: Location) => {
    if (isLiveMode && selectedWarehouse) {
      // Live mode - call backend mutation
      const locationInput = {
        locationCode: location.locationCode,
        warehouseCode: selectedWarehouse.code,
        aisle: location.aisle || undefined,
        rack: location.rack || undefined,
        shelf: location.shelf || undefined,
        bin: location.bin || undefined,
        locationType: location.locationType,
        capacity: location.capacity || undefined,
        pickingSequence: location.pickingSequence || undefined,
      };

      if (editingLocation && location.id) {
        // Update existing location
        const result = await updateLocation(location.id, locationInput);
        if (result.success) {
          toast.success("Location updated successfully");
          // Refresh locations list
          fetchLocations({ warehouseCode: selectedWarehouse.code });
        } else {
          toast.error(result.error || "Failed to update location");
          return;
        }
      } else {
        // Create new location
        const result = await createLocation(locationInput);
        if (result.success) {
          toast.success("Location created successfully");
          // Refresh locations list
          fetchLocations({ warehouseCode: selectedWarehouse.code });
        } else {
          toast.error(result.error || "Failed to create location");
          return;
        }
      }
    } else {
      // Demo mode - update mock data
      if (editingLocation) {
        setMockLocationData(mockLocationData.map((l) => (l.locationCode === location.locationCode ? location : l)));
        toast.success("Location updated successfully");
      } else {
        setMockLocationData([...mockLocationData, location]);
        toast.success("Location created successfully");
      }
    }
    setIsLocationFormOpen(false);
    setEditingLocation(null);
  };

  const handleRowClick = (location: Location) => {
    setSelectedDrawerLocation(location);
    setIsLocationDrawerOpen(true);
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation(location);
    setIsLocationFormOpen(true);
    setIsLocationDrawerOpen(false);
  };

  const handleDeactivateLocation = (location: Location) => {
    setLocationToDeactivate(location);
    setIsDeactivateLocationOpen(true);
    setIsLocationDrawerOpen(false);
  };

  const handleLocationStatusChange = (location: Location, newStatus: string) => {
    // Only in demo mode
    setMockLocationData(
      mockLocationData.map((l) => (l.locationCode === location.locationCode ? { ...l, status: newStatus as any } : l)),
    );
    // Also update the drawer location
    setSelectedDrawerLocation({ ...location, status: newStatus as any });
  };

  const confirmDeactivateLocation = () => {
    if (locationToDeactivate) {
      // Only in demo mode
      setMockLocationData(
        mockLocationData.map((l) =>
          l.locationCode === locationToDeactivate.locationCode ? { ...l, active: false } : l,
        ),
      );
      toast.success("Location deactivated successfully");
      setIsDeactivateLocationOpen(false);
      setLocationToDeactivate(null);
    }
  };

  const handleBulkCreateComplete = async (newLocations: Location[]) => {
    if (isLiveMode && selectedWarehouse) {
      // Live mode - call backend mutation
      const locationInputs = newLocations.map((loc) => ({
        locationCode: loc.locationCode,
        warehouseCode: selectedWarehouse.code,
        aisle: loc.aisle || undefined,
        rack: loc.rack || undefined,
        shelf: loc.shelf || undefined,
        bin: loc.bin || undefined,
        locationType: loc.locationType,
        capacity: loc.capacity || undefined,
        pickingSequence: loc.pickingSequence || undefined,
      }));

      const result = await bulkCreateLocations(locationInputs);
      if (result.success) {
        toast.success(`${newLocations.length} locations created successfully`);
        // Refresh locations list
        fetchLocations({ warehouseCode: selectedWarehouse.code });
      } else {
        toast.error(result.error || "Failed to bulk create locations");
      }
    } else {
      // Demo mode - update mock data
      setMockLocationData([...mockLocationData, ...newLocations]);
      toast.success(`${newLocations.length} locations created successfully`);
    }
  };

  const handleImportComplete = async (newLocations: Location[]) => {
    if (isLiveMode && selectedWarehouse) {
      // Live mode - call backend mutation (same as bulk create)
      const locationInputs = newLocations.map((loc) => ({
        locationCode: loc.locationCode,
        warehouseCode: selectedWarehouse.code,
        aisle: loc.aisle || undefined,
        rack: loc.rack || undefined,
        shelf: loc.shelf || undefined,
        bin: loc.bin || undefined,
        locationType: loc.locationType,
        capacity: loc.capacity || undefined,
        pickingSequence: loc.pickingSequence || undefined,
      }));

      const result = await bulkCreateLocations(locationInputs);
      if (result.success) {
        toast.success(`${newLocations.length} locations imported successfully`);
        // Refresh locations list
        fetchLocations({ warehouseCode: selectedWarehouse.code });
      } else {
        toast.error(result.error || "Failed to import locations");
      }
    } else {
      // Demo mode - update mock data
      setMockLocationData([...mockLocationData, ...newLocations]);
      toast.success(`${newLocations.length} locations imported successfully`);
    }
  };

  const handleApplyFilters = (rows: any[], logic: any[]) => {
    setActiveFilterRows(rows);
    setActiveFilterLogic(logic);
  };

  // Helper function to prepare location data for export with hierarchy level values
  const prepareLocationsForExport = (locs: typeof sortedLocations) => {
    return locs.map((location) => {
      const exportLocation: Record<string, unknown> = { ...location };
      // Add hierarchy level values (level1, level2, etc.) based on the hierarchy config
      // This maps the legacy fields (warehouseCode, aisle, rack, shelf, bin) to level IDs
      hierarchyConfig.activeLevels.forEach((level: { level: number }) => {
        let value: string | undefined;
        // Get value from hierarchyData or legacy fields
        const locAny = location as unknown as Record<string, unknown>;
        if (locAny.hierarchyData) {
          value = (locAny.hierarchyData as Record<number, string>)?.[level.level];
        }
        if (!value) {
          // Fallback to legacy fields for backward compatibility
          // Level 1 = Warehouse, Level 2 = Aisle, Level 3 = Rack, Level 4 = Shelf, Level 5 = Bin
          if (level.level === 1) value = location.warehouseCode;
          else if (level.level === 2) value = location.aisle;
          else if (level.level === 3) value = location.rack;
          else if (level.level === 4) value = location.shelf;
          else if (level.level === 5) value = location.bin;
        }
        exportLocation[`level${level.level}`] = value || "";
      });
      return exportLocation;
    });
  };

  // Export handlers
  const handleExportCurrentViewCSV = async () => {
    if (sortedLocations.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const visibleCols = getVisibleColumns(visibleColumns, ALL_LOCATION_COLUMNS);
      // Prepare locations with hierarchy level values for export
      const locationsWithLevels = prepareLocationsForExport(sortedLocations);
      const preparedData = prepareDataForExport(locationsWithLevels, visibleCols);
      const filename = `locations-${selectedWarehouse?.name.replace(/\s+/g, "-")}-${getCurrentDateForFilename()}.csv`;
      exportToCSV(
        preparedData,
        visibleCols.map((col) => col.name),
        filename,
      );
      toast.success(`${sortedLocations.length} locations exported to CSV`);
    } catch (error) {
      toast.error("Export failed. Please try again.");
      console.error("Export failed with error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportCurrentViewExcel = async () => {
    if (sortedLocations.length === 0) {
      toast.info("No data to export");
      return;
    }

    setIsExporting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const visibleCols = getVisibleColumns(visibleColumns, ALL_LOCATION_COLUMNS);
      // Prepare locations with hierarchy level values for export
      const locationsWithLevels = prepareLocationsForExport(sortedLocations);
      const preparedData = prepareDataForExport(locationsWithLevels, visibleCols);
      const filename = `locations-${selectedWarehouse?.name.replace(/\s+/g, "-")}-${getCurrentDateForFilename()}.xlsx`;
      exportToExcel(
        preparedData,
        visibleCols.map((col) => col.name),
        filename,
      );
      toast.success(`${sortedLocations.length} locations exported to Excel`);
    } catch (error) {
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      if (totalPages > 1) pages.push(totalPages);
    }

    return pages;
  };

  // Calculate warehouse stats
  const warehouseStats = useMemo(() => {
    if (!selectedWarehouse) return null;

    const warehouseLocations = locations.filter((l) => l.warehouseCode === selectedWarehouse.code);
    const totalLocations = warehouseLocations.length;
    const activeLocations = warehouseLocations.filter((l) => l.active).length;

    // Get actual stock items for this warehouse
    const stockItems = getStockItems();
    const warehouseItems = stockItems.filter((item) => item.warehouseCode === selectedWarehouse.code);
    const totalItems = warehouseItems.length;
    const totalStockValue =
      isLiveMode && liveStockValue
        ? liveStockValue.totalStockValue
        : warehouseItems.reduce((sum, item) => sum + (item.value || 0), 0);

    return {
      totalLocations,
      activeLocations,
      totalItems,
      totalStockValue,
    };
  }, [selectedWarehouse, locations, isLiveMode, liveStockValue]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">
              {hierarchyConfig.activeLevels[0]?.displayName || "Locations"}
            </h1>
            {isDemo ? (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Demo Mode
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Live Mode
              </Badge>
            )}
            {(isLoadingWarehouses || isLoadingLocations) && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>
          <p className="text-muted-foreground mt-2">
            Manage warehouses and their location hierarchies (
            {hierarchyConfig.activeLevels.map((l) => l.displayName).join(" > ")}).
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <TestTube2 className={`h-4 w-4 ${isDemo ? "text-yellow-600" : "text-muted-foreground"}`} />
            <Switch checked={isLive} onCheckedChange={handleModeToggle} aria-label="Toggle live mode" />
            <Monitor className={`h-4 w-4 ${isLive ? "text-green-600" : "text-muted-foreground"}`} />
          </div>
          <HelpButton onClick={toggleHelp} showLabel />
        </div>
      </div>

      {/* Warehouse Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          {/* Warehouse Selector */}
          <Select value={selectedWarehouseCode} onValueChange={setSelectedWarehouseCode}>
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select warehouse" />
            </SelectTrigger>
            <SelectContent>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.code} value={warehouse.code}>
                  <div className="flex items-center gap-2">
                    <span>{warehouse.name}</span>
                    {!warehouse.active && (
                      <Badge variant="secondary" className="text-xs">
                        Inactive
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            {selectedWarehouse && (
              <>
                <Button variant="outline" size="sm" onClick={handleEditWarehouse}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit {hierarchyConfig.activeLevels[0]?.displayName || "Warehouse"}
                </Button>
                {selectedWarehouse.active ? (
                  <Button variant="outline" size="sm" onClick={handleDeactivateWarehouse}>
                    <Power className="h-4 w-4 mr-2" />
                    Deactivate
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleActivateWarehouse}>
                    <Power className="h-4 w-4 mr-2" />
                    Activate
                  </Button>
                )}
              </>
            )}
            <Button variant="outline" size="sm" onClick={() => setIsWarehouseTypesSettingsOpen(true)}>
              <Settings2 className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button size="sm" onClick={handleAddWarehouse}>
              <Plus className="h-4 w-4 mr-2" />
              Add {hierarchyConfig.activeLevels[0]?.displayName || "Warehouse"}
            </Button>
          </div>
        </div>

        {/* Warehouse Header + Metric Cards */}
        {selectedWarehouse && warehouseStats && (
          <div className="space-y-4">
            {/* Warehouse Header */}
            <Card className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <Warehouse className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <h2 className="text-xl font-semibold">{selectedWarehouse.name}</h2>
                    <p className="text-sm text-muted-foreground mt-1">{selectedWarehouse.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setIsWarehouseItemsDrawerOpen(true)}>
                    <Package className="h-4 w-4 mr-2" />
                    View All Items
                  </Button>
                  {selectedWarehouse.active ? (
                    <Badge className="bg-green-500 text-white hover:bg-green-600">Active</Badge>
                  ) : (
                    <Badge className="bg-gray-500 text-white hover:bg-gray-600">Inactive</Badge>
                  )}
                  <Badge variant="outline">{selectedWarehouse.type}</Badge>
                </div>
              </div>
            </Card>

            {/* 3 Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Items Card */}
              <StaticMetricCardAlt
                title="Total Items"
                value={warehouseStats.totalItems.toString()}
                change=""
                changeLabel={`${warehouseStats.totalLocations} ${hierarchyConfig.activeLevels[0]?.displayName?.toLowerCase() || "location"}${warehouseStats.totalLocations !== 1 ? "s" : ""} in this ${hierarchyConfig.activeLevels[0]?.displayName?.toLowerCase() || "warehouse"}`}
                icon={Package}
                variant="info"
              />

              {/* Stock Value Card */}
              <StaticMetricCardAlt
                title="Stock Value"
                value={
                  isLiveMode && isLoadingStockValue
                    ? "Loading..."
                    : formatCurrency(warehouseStats.totalStockValue, { compact: true })
                }
                change=""
                changeLabel={isLiveMode ? "Live data from stock levels" : "Demo data (mock calculation)"}
                icon={Package}
                variant="success"
              />

              {/* Last Activity Card */}
              <StaticMetricCardAlt
                title="Last Activity"
                value={(() => {
                  const mostRecentActivity = locations
                    .filter((loc) => loc.warehouseCode === selectedWarehouse?.code)
                    .map((loc) => loc.lastActivityDate)
                    .filter((date) => date != null)
                    .sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];

                  if (!mostRecentActivity) return "Never";

                  const now = new Date();
                  const activityDate = new Date(mostRecentActivity);
                  const diffMs = now.getTime() - activityDate.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMs / 3600000);
                  const diffDays = Math.floor(diffMs / 86400000);
                  const diffWeeks = Math.floor(diffDays / 7);

                  if (diffMins < 1) return "Now";
                  if (diffMins < 60) return `${diffMins}m ago`;
                  if (diffHours < 24) return `${diffHours}h ago`;
                  if (diffDays < 7) return `${diffDays}d ago`;
                  return `${diffWeeks}w ago`;
                })()}
                change=""
                changeLabel={(() => {
                  const mostRecentActivity = locations
                    .filter((loc) => loc.warehouseCode === selectedWarehouse?.code)
                    .map((loc) => loc.lastActivityDate)
                    .filter((date) => date != null)
                    .sort((a, b) => new Date(b!).getTime() - new Date(a!).getTime())[0];

                  return mostRecentActivity ? "Last stock movement" : "No activity recorded yet";
                })()}
                icon={Activity}
                variant="warning"
              />
            </div>
          </div>
        )}
      </div>

      {/* Locations Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <MapPin className="h-5 w-5 text-muted-foreground" />
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-semibold">Locations in {selectedWarehouse?.name || "Warehouse"}</h2>
            {(() => {
              const activeFilters = [searchQuery.trim()].filter(Boolean).length;

              return activeFilters > 0 ? (
                <Badge variant="secondary" className="text-xs">
                  {activeFilters} {activeFilters === 1 ? "filter" : "filters"} active
                </Badge>
              ) : null;
            })()}
          </div>
        </div>

        {/* Action Toolbar */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative w-[300px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by location or item..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <Button variant="outline" onClick={() => setIsAdvancedFiltersOpen(true)} className="relative">
            <Filter className="h-4 w-4 mr-2" />
            Advanced Filters
            {activeAdvancedFiltersCount > 0 && (
              <Badge variant="default" className="ml-2 h-5 min-w-5 px-1.5 bg-teal-600 hover:bg-teal-700">
                {activeAdvancedFiltersCount}
              </Badge>
            )}
          </Button>

          <Button variant="outline" onClick={() => setIsConfigureColumnsModalOpen(true)}>
            <Settings2 className="h-4 w-4 mr-2" />
            Configure Columns
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={isExporting}>
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCurrentViewCSV}>Export to CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportCurrentViewExcel}>Export to Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" onClick={() => setIsBulkCreateOpen(true)} disabled={!selectedWarehouse?.active}>
              <Grid3x3 className="h-4 w-4 mr-2" />
              Bulk Create
            </Button>
            <Button variant="outline" onClick={() => setIsImportOpen(true)} disabled={!selectedWarehouse?.active}>
              <Upload className="h-4 w-4 mr-2" />
              Import
            </Button>
            <Button onClick={handleAddLocation} disabled={!selectedWarehouse?.active}>
              <Plus className="h-4 w-4 mr-2" />
              Add Location
            </Button>
          </div>
        </div>

        {/* Locations Table */}
        {sortedLocations.length > 0 ? (
          <div className="rounded-lg border bg-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={allPageItemsSelected}
                        onCheckedChange={handleSelectAll}
                        className={somePageItemsSelected ? "data-[state=checked]:bg-primary/50" : ""}
                      />
                    </TableHead>
                    {/* Render columns dynamically based on columnOrder */}
                    {getOrderedVisibleColumns().map((columnId) => renderTableHeaderCell(columnId))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedLocations.map((location, index) => (
                    <TableRow
                      key={location.locationCode}
                      className={`${index % 2 === 0 ? "bg-muted/30" : ""} cursor-pointer hover:bg-muted/50`}
                      onClick={() => handleRowClick(location)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedLocations.has(location.locationCode)}
                          onCheckedChange={(checked) => handleSelectLocation(location.locationCode, checked as boolean)}
                        />
                      </TableCell>
                      {/* Render cells dynamically based on columnOrder */}
                      {getOrderedVisibleColumns().map((columnId) => renderTableCell(location, columnId))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between p-4 border-t">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Items per page:</span>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(Number(value));
                    setCurrentPage(1);
                    if (isLiveMode) {
                      setCursors([]);
                      setCurrentCursorIndex(0);
                    }
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground ml-4">
                  {isLiveMode ? (
                    <>
                      Showing {paginatedLocations.length} of {liveStockValue?.totalLocations ?? "..."} locations
                    </>
                  ) : (
                    <>
                      Showing {startItem}-{endItem} of {sortedLocations.length} locations
                    </>
                  )}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {isLiveMode ? (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setCursors([]);
                        setCurrentCursorIndex(0);
                      }}
                      disabled={currentCursorIndex === 0 || isLoadingLocations}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (currentCursorIndex > 0) {
                          setCurrentCursorIndex(currentCursorIndex - 1);
                        }
                      }}
                      disabled={currentCursorIndex === 0 || isLoadingLocations}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {currentCursorIndex + 1}
                      {liveStockValue?.totalLocations
                        ? ` of ${Math.ceil(liveStockValue.totalLocations / itemsPerPage)}`
                        : ""}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        if (locationsPageInfo?.hasNextPage) {
                          setCurrentCursorIndex(currentCursorIndex + 1);
                        }
                      }}
                      disabled={!locationsPageInfo?.hasNextPage || isLoadingLocations}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    {getPageNumbers().map((page, index) => (
                      <Button
                        key={index}
                        variant={page === currentPage ? "default" : "outline"}
                        size="icon"
                        onClick={() => typeof page === "number" && setCurrentPage(page)}
                        disabled={typeof page === "string"}
                      >
                        {page}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Card className="p-12">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="rounded-full bg-muted p-3">
                <MapPin className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="max-w-md">
                <h3 className="text-lg font-semibold mb-2">
                  {searchQuery ? "No locations found" : "No locations configured for this warehouse"}
                </h3>
                {searchQuery ? (
                  <p className="text-sm text-muted-foreground mb-4">
                    Try adjusting your search or filters to find what you're looking for.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      All stock is currently tracked at warehouse level. You can view all items in this warehouse using
                      the "View All Items" button above, or create locations to organize stock within this warehouse.
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <Button variant="outline" onClick={() => setIsWarehouseItemsDrawerOpen(true)}>
                        <Package className="h-4 w-4 mr-2" />
                        View All Items
                      </Button>
                      <Button onClick={handleAddLocation}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Location
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Dialogs */}
      <WarehouseTypesSettingsDialog
        open={isWarehouseTypesSettingsOpen}
        onOpenChange={setIsWarehouseTypesSettingsOpen}
      />

      <WarehouseFormDialog
        open={isWarehouseFormOpen}
        onOpenChange={setIsWarehouseFormOpen}
        warehouse={editingWarehouse}
        warehouses={warehouses}
        locations={locations}
        onSave={handleSaveWarehouse}
      />

      <LocationFormDialog
        open={isLocationFormOpen}
        onOpenChange={setIsLocationFormOpen}
        location={editingLocation}
        warehouseCode={selectedWarehouseCode}
        warehouses={warehouses}
        existingLocations={locations}
        onSave={handleSaveLocation}
      />

      <LocationDetailsDrawer
        open={isLocationDrawerOpen}
        onOpenChange={setIsLocationDrawerOpen}
        location={selectedDrawerLocation}
        onEdit={handleEditLocation}
        onDeactivate={handleDeactivateLocation}
        onStatusChange={handleLocationStatusChange}
        isLiveMode={isLiveMode}
        onDeactivateLocation={deactivateLocation}
        onBlockLocation={blockLocation}
        onUnblockLocation={unblockLocation}
        onRefreshLocations={() => {
          if (selectedWarehouseCode) {
            fetchLocations({ warehouseCode: selectedWarehouseCode });
          }
        }}
      />

      <WarehouseItemsDrawer
        open={isWarehouseItemsDrawerOpen}
        onOpenChange={setIsWarehouseItemsDrawerOpen}
        warehouse={selectedWarehouse}
      />

      <BulkCreateLocationsDialog
        open={isBulkCreateOpen}
        onOpenChange={setIsBulkCreateOpen}
        warehouseCode={selectedWarehouseCode}
        existingLocations={locations}
        onComplete={handleBulkCreateComplete}
        isLive={isLiveMode}
      />

      <ImportLocationsDialog
        open={isImportOpen}
        onOpenChange={setIsImportOpen}
        warehouses={warehouses}
        existingLocations={locations}
        onComplete={handleImportComplete}
        isLive={isLiveMode}
      />

      <DeactivateWarehouseDialog
        open={isDeactivateWarehouseOpen}
        onOpenChange={setIsDeactivateWarehouseOpen}
        warehouse={warehouseToDeactivate}
        hasLocations={
          warehouseToDeactivate ? locations.some((l) => l.warehouseCode === warehouseToDeactivate.code) : false
        }
        onConfirm={confirmDeactivateWarehouse}
      />

      <DeactivateLocationDialog
        open={isDeactivateLocationOpen}
        onOpenChange={setIsDeactivateLocationOpen}
        location={locationToDeactivate}
        onConfirm={confirmDeactivateLocation}
      />

      <ConfigureColumnsModal
        open={isConfigureColumnsModalOpen}
        onOpenChange={setIsConfigureColumnsModalOpen}
        columns={ALL_LOCATION_COLUMNS}
        currentConfig={{ visibleColumns, columnOrder }}
        defaultConfig={{
          visibleColumns: DEFAULT_VISIBLE_LOCATION_COLUMNS,
          columnOrder: DEFAULT_VISIBLE_LOCATION_COLUMNS,
        }}
        onSave={(config) => {
          setVisibleColumns(config.visibleColumns);
          setColumnOrder(config.columnOrder);
          toast.success("Column configuration saved");
        }}
      />

      <AdvancedFiltersModal
        open={isAdvancedFiltersOpen}
        onOpenChange={setIsAdvancedFiltersOpen}
        onApplyFilters={handleApplyFilters}
        initialFilters={activeFilterRows}
        initialLogic={activeFilterLogic}
        entityType="locations"
        customFields={dynamicFilterFields}
      />

      <ContextualHelpDrawer open={isHelpOpen} onOpenChange={setIsHelpOpen} helpContent={helpContent} />
    </div>
  );
}
